import React, { useState, useEffect, useRef, useCallback } from 'react';
import Coachmark from './tutorial/Coachmark';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import './Fishing.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { authButtonStyle, authStatPanelStyle, textboxFrameStyle } from '../constants/authStyles';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { formatDateTime } from '../utils/localeFormat';
import { useCharacterOptional } from '../context/CharacterContext';
import { useGameConfig } from '../context/GameConfigContext';
import { FISHING_MIN_LEVEL } from '../constants/fishing';
import { meetsMinLevel } from '../utils/levelRequirement';
import { ProfessionBadge } from './ProfessionPanels';

const FISHING_SOUND = '/sounds/fishing.mp3';

const SCENE_IDLE = 'idle';
const SCENE_CASTING = 'casting';
const SCENE_IN_WATER = 'in_water';
const SCENE_CATCH_OK = 'catch_ok';
const SCENE_CATCH_FAIL = 'catch_fail';

const SCENE_IMAGES = {
  [SCENE_IDLE]: '/image/fishing/bos.png',
  [SCENE_CASTING]: '/image/fishing/atarken.png',
  [SCENE_IN_WATER]: '/image/fishing/oltasuda.png',
  [SCENE_CATCH_OK]: '/image/fishing/baliktuttu.png',
  [SCENE_CATCH_FAIL]: '/image/fishing/baliktutamadi.png',
};

const SCENE_BACKGROUNDS = {
  [SCENE_CASTING]: '/image/fishing/bg.png',
  [SCENE_CATCH_OK]: '/image/fishing/bg.png',
  [SCENE_CATCH_FAIL]: '/image/fishing/bg.png',
};

const SPRITE_SCENES = new Set([SCENE_CASTING, SCENE_CATCH_OK, SCENE_CATCH_FAIL]);
const SPRITE_FRAME_W = 300;
const SPRITE_FRAME_H = 200;
const SPRITE_FRAMES = 5;
const SPRITE_SHEET_W = SPRITE_FRAME_W * SPRITE_FRAMES;
const SPRITE_FPS = 5;
const SPRITE_FRAME_MS = 1000 / SPRITE_FPS;
const SPRITE_LAST_FRAME_HOLD_MS = 2000;
const CATCH_FLY_MS = 900;

const fishImageUrlForSpecies = (speciesId) => {
  if (!speciesId || typeof speciesId !== 'string') return null;
  const id = speciesId.trim().toLowerCase();
  if (!id) return null;
  return `/image/others/${id}.png`;
};

const FISHING_IMAGE_URLS = [
  ...new Set([
    ...Object.values(SCENE_IMAGES),
    ...Object.values(SCENE_BACKGROUNDS),
  ]),
];

const fishingImageCache = new Map();

const waitForNextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

const ensureFishingImagesLoaded = (urls) => {
  const list = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
  return Promise.all(list.map((url) => {
    if (fishingImageCache.get(url) === 'loaded') {
      return Promise.resolve();
    }
    const pending = fishingImageCache.get(url);
    if (pending && typeof pending.then === 'function') {
      return pending;
    }
    const promise = new Promise((resolve) => {
      const img = new Image();
      const finish = async () => {
        try {
          if (typeof img.decode === 'function') {
            await img.decode();
          }
        } catch (_) {
          // decode can fail on some browsers; onload is still enough to continue
        }
        fishingImageCache.set(url, 'loaded');
        resolve();
      };
      img.onload = () => { finish(); };
      img.onerror = () => {
        fishingImageCache.set(url, 'loaded');
        resolve();
      };
      img.src = url;
    });
    fishingImageCache.set(url, promise);
    return promise;
  }));
};

const spriteSceneAssets = (nextScene) => [
  SCENE_BACKGROUNDS[nextScene],
  SCENE_IMAGES[nextScene],
];

const barRangeOf = (mg) => Math.max(1, mg?.barRangeMax ?? 50);
const visualPctOf = (pos, range) => (pos / range) * 100;

const fishGreenParamsOf = (fishMg, globalMg) => ({
  greenZoneWidthPercent: fishMg?.greenZoneWidthPercent ?? globalMg?.greenZoneWidthPercent ?? 22,
  greenMoveSpeed: fishMg?.greenMoveSpeed ?? globalMg?.greenMoveSpeed ?? 5,
  greenMoveChangeMinMs: fishMg?.greenMoveChangeMinMs ?? globalMg?.greenMoveChangeMinMs ?? 900,
  greenMoveChangeMaxMs: fishMg?.greenMoveChangeMaxMs ?? globalMg?.greenMoveChangeMaxMs ?? 2400,
});

const randomGreenChangeDelay = (params) => {
  const min = params.greenMoveChangeMinMs;
  const max = Math.max(min, params.greenMoveChangeMaxMs);
  return min + Math.random() * (max - min);
};

const pickRandomGreenDir = () => (Math.random() < 0.5 ? -1 : 1);

const formatMsAsSeconds = (ms) => (Math.max(0, ms) / 1000).toFixed(1);

const formatGuideRemaining = (untilUnix) => {
  if (!untilUnix || untilUnix <= Math.floor(Date.now() / 1000)) return null;
  return formatDateTime(untilUnix * 1000, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Fishing = () => {
  const navigate = useNavigate();
  const { t: tg } = useTranslation('gameData');
  const { t: ts } = useTranslation('selection');
  const username = localStorage.getItem('username');
  const [hasRod, setHasRod] = useState(false);
  const [equipmentLoaded, setEquipmentLoaded] = useState(false);
  const [rodEnh, setRodEnh] = useState(0);
  const [fishCaught, setFishCaught] = useState(0);
  const [fishingGuideUntil, setFishingGuideUntil] = useState(0);
  const [config, setConfig] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const { setError, setStatus } = usePopupMessage();
  const characterCtx = useCharacterOptional();
  const { gelistirmeConsole } = useGameConfig();
  const setDepotItemCount = characterCtx?.setDepotItemCount;
  const characterLevel = characterCtx?.character?.level ?? 0;
  const characterLoading = characterCtx?.loading;
  const fishingLevelOk = meetsMinLevel(characterLevel, FISHING_MIN_LEVEL, gelistirmeConsole);
  const [busy, setBusy] = useState(false);
  const [minigameActive, setMinigameActive] = useState(false);
  const [activeFishMinigame, setActiveFishMinigame] = useState(null);
  const [hookedFishName, setHookedFishName] = useState('');
  const [hookedFishSpeciesId, setHookedFishSpeciesId] = useState('');
  const [dropNotifications, setDropNotifications] = useState([]);
  const [catchFlyItems, setCatchFlyItems] = useState([]);
  const dropNotificationTimeoutsRef = useRef({});
  const lakeVisualRef = useRef(null);
  const inventoryBtnRef = useRef(null);
  const catchFlyTimeoutsRef = useRef({});
  const [scene, setScene] = useState(SCENE_IDLE);
  const [spriteFrame, setSpriteFrame] = useState(0);
  const [greenMsDisplay, setGreenMsDisplay] = useState(0);
  const [redMsDisplay, setRedMsDisplay] = useState(0);
  const sessionRef = useRef({ token: null });
  const holdingRef = useRef(false);
  const posRef = useRef(0.5);
  const velRef = useRef(0);
  const greenMsRef = useRef(0);
  const redMsRef = useRef(0);
  const greenCenterRef = useRef(50);
  const greenDirRef = useRef(1);
  const greenNextChangeRef = useRef(0);
  const lastTRef = useRef(null);
  const rafRef = useRef(null);
  const endedRef = useRef(false);
  const minigameActiveRef = useRef(false);
  const configRef = useRef(null);
  const spriteTimerRef = useRef(null);
  const spriteHoldTimerRef = useRef(null);
  const spriteResolveRef = useRef(null);
  const fishingAudioRef = useRef(null);
  const sceneRef = useRef(SCENE_IDLE);
  const [barTick, setBarTick] = useState(0);
  const [underlayScene, setUnderlayScene] = useState(null);

  useEffect(() => {
    ensureFishingImagesLoaded(FISHING_IMAGE_URLS);
  }, []);

  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  useEffect(() => {
    minigameActiveRef.current = minigameActive;
  }, [minigameActive]);

  useEffect(() => {
    if (!minigameActive) return;
    ensureFishingImagesLoaded([
      ...spriteSceneAssets(SCENE_CATCH_OK),
      ...spriteSceneAssets(SCENE_CATCH_FAIL),
    ]);
  }, [minigameActive]);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    const audio = new Audio(FISHING_SOUND);
    audio.loop = true;
    audio.preload = 'auto';
    fishingAudioRef.current = audio;
    return () => {
      audio.pause();
      fishingAudioRef.current = null;
    };
  }, []);

  const stopSpritePlayback = useCallback(() => {
    if (spriteTimerRef.current) {
      clearInterval(spriteTimerRef.current);
      spriteTimerRef.current = null;
    }
    if (spriteHoldTimerRef.current) {
      clearTimeout(spriteHoldTimerRef.current);
      spriteHoldTimerRef.current = null;
    }
    if (spriteResolveRef.current) {
      const resolve = spriteResolveRef.current;
      spriteResolveRef.current = null;
      resolve();
    }
  }, []);

  const runSpriteAnimation = useCallback(
    ({ holdLastMs = 0 } = {}) =>
      new Promise((resolve) => {
        stopSpritePlayback();
        let frame = 0;
        spriteResolveRef.current = resolve;

        const finish = () => {
          spriteResolveRef.current = null;
          resolve();
        };

        const holdThenFinish = () => {
          if (holdLastMs > 0) {
            setSpriteFrame(SPRITE_FRAMES - 1);
            spriteHoldTimerRef.current = setTimeout(() => {
              spriteHoldTimerRef.current = null;
              finish();
            }, holdLastMs);
            return;
          }
          finish();
        };

        setSpriteFrame(0);
        spriteTimerRef.current = setInterval(() => {
          frame += 1;
          if (frame >= SPRITE_FRAMES) {
            if (spriteTimerRef.current) {
              clearInterval(spriteTimerRef.current);
              spriteTimerRef.current = null;
            }
            holdThenFinish();
            return;
          }
          setSpriteFrame(frame);
        }, SPRITE_FRAME_MS);
      }),
    [stopSpritePlayback]
  );

  const transitionToSpriteScene = useCallback(async (nextScene) => {
    const currentScene = sceneRef.current;
    if (!SPRITE_SCENES.has(currentScene) && SPRITE_SCENES.has(nextScene)) {
      setUnderlayScene(currentScene);
    }
    await ensureFishingImagesLoaded(spriteSceneAssets(nextScene));
    await waitForNextPaint();
    setScene(nextScene);
    setSpriteFrame(0);
    await waitForNextPaint();
    setUnderlayScene(null);
  }, []);

  const playSpriteOnce = useCallback(
    async (nextScene, { holdLastMs = 0 } = {}) => {
      await transitionToSpriteScene(nextScene);
      await runSpriteAnimation({ holdLastMs });
    },
    [transitionToSpriteScene, runSpriteAnimation]
  );

  const setIdleSceneWhenReady = useCallback(
    () => ensureFishingImagesLoaded(SCENE_IMAGES[SCENE_IDLE]).then(() => {
      setScene(SCENE_IDLE);
      setSpriteFrame(0);
    }),
    []
  );

  useEffect(() => () => stopSpritePlayback(), [stopSpritePlayback]);

  useEffect(() => {
    const timeouts = dropNotificationTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  useEffect(() => {
    const timeouts = catchFlyTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  const spawnCatchFlyAnimation = useCallback((speciesId) => {
    const imageUrl = fishImageUrlForSpecies(speciesId);
    const fromEl = lakeVisualRef.current;
    const toEl = inventoryBtnRef.current;
    if (!imageUrl || !fromEl || !toEl) return;

    const fromRect = fromEl.getBoundingClientRect();
    const toRect = toEl.getBoundingClientRect();
    const startX = fromRect.left + fromRect.width / 2;
    const startY = fromRect.bottom;
    const endX = toRect.left + toRect.width / 2;
    const endY = toRect.top + toRect.height / 2;
    const id = Date.now() + Math.random();

    setCatchFlyItems((prev) => [
      ...prev,
      {
        id,
        imageUrl,
        startX,
        startY,
        deltaX: endX - startX,
        deltaY: endY - startY,
      },
    ]);

    const timeoutId = setTimeout(() => {
      setCatchFlyItems((prev) => prev.filter((item) => item.id !== id));
      delete catchFlyTimeoutsRef.current[id];
    }, CATCH_FLY_MS + 50);
    catchFlyTimeoutsRef.current[id] = timeoutId;
  }, []);

  const addDropNotification = useCallback((message) => {
    if (!message) return;
    const id = Date.now() + Math.random();
    setDropNotifications((prev) => {
      const next = [...prev, { id, message }];
      if (next.length <= 3) return next;
      const removed = next.shift();
      if (removed?.id && dropNotificationTimeoutsRef.current[removed.id]) {
        clearTimeout(dropNotificationTimeoutsRef.current[removed.id]);
        delete dropNotificationTimeoutsRef.current[removed.id];
      }
      return next;
    });
    const timeoutId = setTimeout(() => {
      setDropNotifications((prev) => prev.filter((n) => n.id !== id));
      delete dropNotificationTimeoutsRef.current[id];
    }, 3000);
    dropNotificationTimeoutsRef.current[id] = timeoutId;
  }, []);

  const pauseReelSound = useCallback(() => {
    const audio = fishingAudioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }, []);

  const playReelSound = useCallback(() => {
    const audio = fishingAudioRef.current;
    if (!audio || !audio.paused) return;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }, []);

  const refreshEquipment = useCallback(async () => {
    const res = await axios.get(`/api/fishing/equipment?username=${encodeURIComponent(username)}`);
    if (res.data?.success) {
      setHasRod(res.data.hasFishingRod);
      setRodEnh(res.data.rodEnhancement ?? 0);
      setFishCaught(res.data.fishCaught ?? 0);
      setFishingGuideUntil(res.data.fishingGuideUntil ?? 0);
    }
  }, [username]);

  const load = useCallback(async () => {
    if (!username) {
      navigate('/login');
      return;
    }
    try {
      const [eqRes, cfgRes] = await Promise.all([
        axios.get(`/api/fishing/equipment?username=${encodeURIComponent(username)}`),
        axios.get('/api/fishing/config'),
      ]);
      if (eqRes.data?.success) {
        setHasRod(eqRes.data.hasFishingRod);
        setRodEnh(eqRes.data.rodEnhancement ?? 0);
        setFishCaught(eqRes.data.fishCaught ?? 0);
        setFishingGuideUntil(eqRes.data.fishingGuideUntil ?? 0);
      }
      if (cfgRes.data?.success && cfgRes.data.config) {
        setConfig(cfgRes.data.config);
      }
    } catch (e) {
      console.error(e);
      setError(tg('fishingServerError'));
    } finally {
      setEquipmentLoaded(true);
    }
  }, [username, navigate, setError, tg]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!username) return;
    if (characterLoading) return;
    if (!fishingLevelOk) {
      navigate('/work', { replace: true });
    }
  }, [username, characterLoading, fishingLevelOk, navigate]);

  const applyTapNudge = useCallback(() => {
    const mg = configRef.current?.minigame;
    const range = barRangeOf(mg);
    const pct = mg?.tapNudgePercent ?? 0.6;
    posRef.current = Math.min(range * 0.97, posRef.current + (pct / 100) * range);
    velRef.current *= 0.05;
    setBarTick((x) => x + 1);
  }, []);

  useEffect(() => {
    const onPointerUp = () => {
      holdingRef.current = false;
      pauseReelSound();
    };
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [pauseReelSound]);

  const finishMinigame = useCallback(
    async (success) => {
      if (endedRef.current) return;
      endedRef.current = true;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      holdingRef.current = false;
      pauseReelSound();
      setMinigameActive(false);
      setActiveFishMinigame(null);
      setBusy(true);
      lastTRef.current = null;
      try {
        const resultScene = success ? SCENE_CATCH_OK : SCENE_CATCH_FAIL;
        const apiPromise = axios.post('/api/fishing/complete', {
          username,
          sessionToken: sessionRef.current.token,
          success,
        });
        await transitionToSpriteScene(resultScene);
        const [res] = await Promise.all([
          apiPromise,
          runSpriteAnimation({ holdLastMs: SPRITE_LAST_FRAME_HOLD_MS }),
        ]);
        if (success) {
          spawnCatchFlyAnimation(hookedFishSpeciesId || res.data?.speciesId);
        }
        await setIdleSceneWhenReady();
        const speciesName = res.data?.speciesName || hookedFishName || '';
        if (success) {
          if (typeof res.data?.depotItemCount === 'number') {
            setDepotItemCount?.(res.data.depotItemCount);
          }
          if (speciesName) {
            addDropNotification(tg('fishingGainNotify', { species: speciesName }));
          }
          const catchMsg = speciesName
            ? tg('fishingCaughtFish', { species: speciesName })
            : (res.data?.message || tg('fishingCaughtFish', { species: tg('fishingRod') }));
          setStatusMsg(tg('fishingCast'));
          if (res.data?.message) {
            setStatus(res.data.message);
          } else {
            setStatus(catchMsg);
          }
        } else {
          const escapeMsg = res.data?.message || tg('fishingEscaped');
          setStatusMsg(escapeMsg);
        }
        setHookedFishName('');
        setHookedFishSpeciesId('');
        await refreshEquipment();
      } catch (err) {
        const msg = err.response?.data?.message || err.message || tg('fishingCompleteFailed');
        setError(msg);
        setStatusMsg(msg);
        setHookedFishName('');
        setHookedFishSpeciesId('');
        setUnderlayScene(null);
        stopSpritePlayback();
        await setIdleSceneWhenReady();
      } finally {
        sessionRef.current.token = null;
        setBusy(false);
      }
    },
    [username, refreshEquipment, setError, setStatus, tg, transitionToSpriteScene, runSpriteAnimation, stopSpritePlayback, pauseReelSound, hookedFishName, hookedFishSpeciesId, addDropNotification, spawnCatchFlyAnimation, setDepotItemCount, setIdleSceneWhenReady]
  );

  useEffect(() => {
    if (!minigameActive || !config?.minigame) return;
    const mg = config.minigame;
    const fishGreen = fishGreenParamsOf(activeFishMinigame, mg);
    const range = barRangeOf(mg);

    endedRef.current = false;
    posRef.current = range * 0.5;
    velRef.current = 0;
    greenMsRef.current = 0;
    redMsRef.current = 0;
    setGreenMsDisplay(0);
    setRedMsDisplay(0);
    lastTRef.current = null;
    greenCenterRef.current = 50;
    greenDirRef.current = pickRandomGreenDir();
    greenNextChangeRef.current = performance.now() + randomGreenChangeDelay(fishGreen);

    const scheduleGreenDirChange = (now) => {
      greenDirRef.current = pickRandomGreenDir();
      greenNextChangeRef.current = now + randomGreenChangeDelay(fishGreen);
    };

    const tick = (t) => {
      if (endedRef.current) return;
      if (lastTRef.current == null) lastTRef.current = t;
      const dt = Math.min(48, t - lastTRef.current);
      lastTRef.current = t;

      const w = fishGreen.greenZoneWidthPercent;
      const minCenter = w / 2 + 2;
      const maxCenter = 100 - w / 2 - 2;
      let center = greenCenterRef.current + greenDirRef.current * fishGreen.greenMoveSpeed * (dt / 1000);
      if (center <= minCenter) {
        center = minCenter;
        greenDirRef.current = 1;
        scheduleGreenDirChange(t);
      } else if (center >= maxCenter) {
        center = maxCenter;
        greenDirRef.current = -1;
        scheduleGreenDirChange(t);
      } else if (t >= greenNextChangeRef.current) {
        scheduleGreenDirChange(t);
      }
      greenCenterRef.current = center;

      const gLeft = greenCenterRef.current - w / 2;
      const gRight = greenCenterRef.current + w / 2;
      const indPct = visualPctOf(posRef.current, range);
      const inGreen = indPct >= gLeft && indPct <= gRight;
      if (inGreen) greenMsRef.current += dt;
      else redMsRef.current += dt;
      setGreenMsDisplay(greenMsRef.current);
      setRedMsDisplay(redMsRef.current);

      const maxV = mg.maxVelocity ?? 0.035;
      const posScale = mg.positionDeltaScale ?? 0.0025;
      const holdMul = mg.holdAccelMultiplier ?? 0.4;
      if (holdingRef.current) {
        velRef.current += (mg.indicatorAccelRight || 0.000008) * dt * holdMul;
      } else {
        velRef.current -= (mg.indicatorAccelLeft || 0.000005) * dt;
      }
      velRef.current = Math.max(-maxV, Math.min(maxV, velRef.current));
      posRef.current += velRef.current * dt * posScale;
      posRef.current = Math.max(range * 0.03, Math.min(range * 0.97, posRef.current));

      setBarTick((x) => x + 1);

      if (greenMsRef.current >= (mg.greenHoldMs || 5000)) {
        finishMinigame(true);
        return;
      }
      if (redMsRef.current >= (mg.redFailMs || 3000)) {
        finishMinigame(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      endedRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [minigameActive, config, activeFishMinigame, finishMinigame]);

  useEffect(() => {
    if (!equipmentLoaded || characterLoading || !fishingLevelOk) return;
    if (!hasRod) {
      setError(tg('fishingNoRod'));
    }
  }, [equipmentLoaded, hasRod, characterLoading, fishingLevelOk, setError, tg]);

  const handleCast = async () => {
    if (busy || minigameActive || !hasRod) return;
    setBusy(true);
    setStatusMsg(tg('fishingCasting'));
    setHookedFishName('');
    setHookedFishSpeciesId('');
    setGreenMsDisplay(0);
    setRedMsDisplay(0);
    greenMsRef.current = 0;
    redMsRef.current = 0;
    try {
      await playSpriteOnce(SCENE_CASTING);
      await ensureFishingImagesLoaded(SCENE_IMAGES[SCENE_IN_WATER]);
      setScene(SCENE_IN_WATER);
      setSpriteFrame(0);
      setStatusMsg(tg('fishingWaitingBite'));

      const res = await axios.post('/api/fishing/cast', { username });
      const data = res.data || {};

      // Olta suda: tutup tutmadığını göstermeden önce bekle
      await new Promise((resolve) => setTimeout(resolve, 3000));

      if (data.phase === 'level_locked') {
        const msg = data.message || tg('fishingLevelLocked', { level: FISHING_MIN_LEVEL });
        setError(msg);
        setStatusMsg(msg);
        navigate('/work', { replace: true });
        return;
      }
      if (data.phase === 'no_rod' || (data.success === false && !data.phase)) {
        const msg = data.message || tg('fishingNoRodEquipped');
        setError(msg);
        setStatusMsg(msg);
        await setIdleSceneWhenReady();
        return;
      }
      if (data.phase === 'minigame' && data.sessionToken) {
        sessionRef.current.token = data.sessionToken;
        setActiveFishMinigame(data.minigameParams ?? null);
        const fishName = data.fishName || '';
        setHookedFishName(fishName);
        setHookedFishSpeciesId(data.fishSpeciesId || '');
        setStatusMsg(fishName
          ? tg('fishingHookedFish', { fishName })
          : tg('fishingMinigameGoal', { fishName: tg('fishingRod') }));
        setMinigameActive(true);
        return;
      }
      // no_bite / nibble_no_hook vb. — status'ta kalsın (boşta da dursun)
      setStatusMsg(data.message || tg('fishingNothingHappened'));
      await setIdleSceneWhenReady();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || tg('fishingCastFailed');
      setError(msg);
      setStatusMsg(msg);
      setHookedFishSpeciesId('');
      await setIdleSceneWhenReady();
    } finally {
      setBusy(false);
    }
  };

  const barRange = barRangeOf(config?.minigame);
  const indicatorLeft =
    config?.minigame && barTick >= 0 ? visualPctOf(posRef.current, barRange) : 50;

  const fishGreen = fishGreenParamsOf(activeFishMinigame, config?.minigame);
  const greenLeft =
    config?.minigame && barTick >= 0
      ? greenCenterRef.current - fishGreen.greenZoneWidthPercent / 2
      : 39;
  const greenW = fishGreen.greenZoneWidthPercent;

  const sceneImage = SCENE_IMAGES[scene] || SCENE_IMAGES[SCENE_IDLE];
  const isSpriteScene = SPRITE_SCENES.has(scene);
  const spriteBackgroundPosition = `-${spriteFrame * SPRITE_FRAME_W}px 0`;

  const renderLakeVisual = () => {
    const fullImage = SCENE_IMAGES[scene] || SCENE_IMAGES[SCENE_IDLE];
    const sceneBg = isSpriteScene
      ? (SCENE_BACKGROUNDS[scene] || SCENE_BACKGROUNDS[SCENE_CATCH_FAIL])
      : fullImage;

    const underlayImage = underlayScene
      ? (SCENE_IMAGES[underlayScene] || SCENE_IMAGES[SCENE_IDLE])
      : null;

    return (
      <div
        ref={lakeVisualRef}
        className="fishing-lake-visual fishing-lake-visual--layered"
        aria-hidden="true"
      >
        {underlayImage && (
          <div
            className="fishing-lake-visual__bg fishing-lake-visual__bg--underlay"
            style={{ backgroundImage: `url("${underlayImage}")` }}
          />
        )}
        <div
          className="fishing-lake-visual__bg"
          style={{ backgroundImage: `url("${sceneBg}")` }}
        />
        {isSpriteScene && (
          <div
            className="fishing-lake-visual__sprite"
            style={{
              backgroundImage: `url("${sceneImage}")`,
              backgroundSize: `${SPRITE_SHEET_W}px ${SPRITE_FRAME_H}px`,
              backgroundPosition: spriteBackgroundPosition,
            }}
          />
        )}
      </div>
    );
  };

  const renderToolbar = () => (
    <AuthSelectionToolbar
      className="fishing-toolbar"
      onBack={() => navigate('/work')}
      backLabel={tg('fishingBackToMenu')}
      buttonStyle={authButtonStyle}
      leadingExtra={(
        <button
          ref={inventoryBtnRef}
          type="button"
          className="auth-button auth-button--compact"
          style={authButtonStyle}
          onClick={() => navigate('/inventory', { state: { from: '/fishing' } })}
        >
          {ts('menuInventory')}
        </button>
      )}
    />
  );

  if (characterLoading || !fishingLevelOk) {
    return (
      <div className="auth-container auth-container--selection">
        <AuthLayeredCard>
          {renderToolbar()}
          <div className="auth-selection-body fishing-body">
            <p className="fishing-warn">
              {tg('fishingLevelLocked', { level: FISHING_MIN_LEVEL })}
            </p>
          </div>
        </AuthLayeredCard>
      </div>
    );
  }

  const guideExpiry = formatGuideRemaining(fishingGuideUntil);
  const guideActive = fishingGuideUntil > Math.floor(Date.now() / 1000);

  return (
    <div className="auth-container auth-container--selection">
      <Coachmark
        tourId="fishing"
        steps={[
          { selector: '.fishing-actions', textKey: 'fishingActions' },
      ]}
      />
      {catchFlyItems.map((item) => (
        <img
          key={item.id}
          className="fishing-catch-fly"
          src={item.imageUrl}
          alt=""
          aria-hidden="true"
          style={{
            '--fly-start-x': `${item.startX}px`,
            '--fly-start-y': `${item.startY}px`,
            '--fly-delta-x': `${item.deltaX}px`,
            '--fly-delta-y': `${item.deltaY}px`,
          }}
        />
      ))}
      {dropNotifications.length > 0 && (
        <div className="drop-notifications-container fishing-drop-notifications">
          {dropNotifications.map((notification) => (
            <div key={notification.id} className="drop-notification">
              {notification.message}
            </div>
          ))}
        </div>
      )}
      <AuthLayeredCard>
        {renderToolbar()}
        <ProfessionBadge skill="fishing" />

        <div className="auth-selection-body fishing-body">
          <div className="fishing-stats-row">
            <div className="fishing-stat-panel fishing-stat-panel--inline" style={authStatPanelStyle}>
              <div className="fishing-stat-panel__inline-row">
                <span className="fishing-stat-panel__emoji fishing-stat-panel__emoji--text">
                  {tg('fishingEquipment')}
                </span>
                <span className="fishing-stat-panel__value">{hasRod ? `+${rodEnh}` : '—'}</span>
              </div>
            </div>
            <div className="fishing-stat-panel fishing-stat-panel--inline" style={authStatPanelStyle}>
              <div className="fishing-stat-panel__inline-row">
                <span className="fishing-stat-panel__emoji" aria-hidden="true">
                  🐟
                </span>
                <span className="fishing-stat-panel__label">{tg('fishingCaught')}</span>
                <span className="fishing-stat-panel__value">{fishCaught}</span>
              </div>
            </div>
            {guideActive && guideExpiry && (
              <div className="fishing-stat-panel fishing-stat-panel--inline fishing-stat-panel--guide" style={authStatPanelStyle}>
                <div className="fishing-stat-panel__inline-row">
                  <span className="fishing-stat-panel__label">{tg('fishingGuideActive')}</span>
                  <span className="fishing-stat-panel__value fishing-stat-panel__value--small">
                    {tg('fishingGuideUntil', { expiry: guideExpiry })}
                  </span>
                </div>
              </div>
            )}
          </div>

          <section className="fishing-lake-section">
            <div
              className="fishing-lake-frame"
              style={textboxFrameStyle}
            >
              <div className="fishing-lake-visual-zoom">
                {renderLakeVisual()}
              </div>
            </div>
            <div className="fishing-status-panel fishing-status-panel--under-visual" style={authStatPanelStyle}>
              <p className={`fishing-status-text${!hasRod ? ' fishing-status-text--warn' : ''}`}>
                {!hasRod ? tg('fishingNoRod') : (statusMsg || tg('fishingCast'))}
              </p>
            </div>
          </section>

          <div className={`fishing-minigame${minigameActive ? '' : ' fishing-minigame--disabled'}`}>
            <div
              className="fishing-bar-frame"
              style={textboxFrameStyle}
            >
              <div className="fishing-bar-track">
                <div
                  className="fishing-zone-green"
                  style={{
                    left: `${greenLeft}%`,
                    width: `${greenW}%`,
                  }}
                />
                <div
                  className="fishing-indicator"
                  style={{ left: `${indicatorLeft}%` }}
                />
              </div>
            </div>
            <div className="fishing-minigame-timers">
              <span>{tg('fishingTimerInside', { seconds: formatMsAsSeconds(greenMsDisplay) })}</span>
              <span>{tg('fishingTimerOutside', { seconds: formatMsAsSeconds(redMsDisplay) })}</span>
            </div>
            <p className="fishing-minigame-hint">
              {config?.minigame
                ? tg('fishingMinigameHint', {
                    greenSeconds: Math.round((config.minigame.greenHoldMs || 5000) / 1000),
                    redSeconds: Math.round((config.minigame.redFailMs || 3000) / 1000),
                  })
                : tg('fishingMinigameIdleHint')}
            </p>
          </div>

          <div className="fishing-actions">
            <button
              type="button"
              className={`auth-button fishing-action-btn ${minigameActive ? 'fishing-action-btn--hold' : ''}`}
              style={authButtonStyle}
              disabled={!hasRod || (busy && !minigameActive)}
              data-silent-click={minigameActive ? 'true' : undefined}
              onPointerDown={(e) => {
                if (!minigameActive) return;
                e.preventDefault();
                try {
                  e.currentTarget.setPointerCapture(e.pointerId);
                } catch (_) {}
                holdingRef.current = true;
                playReelSound();
                applyTapNudge();
              }}
              onPointerUp={() => {
                holdingRef.current = false;
                pauseReelSound();
              }}
              onPointerCancel={() => {
                holdingRef.current = false;
                pauseReelSound();
              }}
              onClick={() => {
                if (!minigameActive) handleCast();
              }}
            >
              {minigameActive ? tg('fishingReel') : busy ? '…' : tg('fishingCast')}
            </button>
          </div>
        </div>
      </AuthLayeredCard>
    </div>
  );
};

export default Fishing;
